import React from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Button } from "ui/button";
import { mergeSx } from "ui/merge-sx";

import { Comment } from "types/comments";
import { PropsAs } from "types/utility";

import Icons from "assets";

const TRIGGER_SX: SxProps<Theme> = (theme) => ({
  position: "absolute",
  top: theme.spacing(-1.5),
  left: theme.spacing(-1.5),
  py: 0.5,
  gap: 0.25,
  borderRadius: "9999px",
  fontSize: 13,
  "& svg": { width: 16, height: 16 },
});

const BADGE_TRIGGER_SX: SxProps<Theme> = (theme) => ({ py: 0.25, left: theme.spacing(-3) });

const HIDDEN_TRIGGER_SX: SxProps<Theme> = { opacity: 0 };

const INERT_TRIGGER_SX: SxProps<Theme> = { pointerEvents: "none" };

const INTERACTIVE_TRIGGER_SX: SxProps<Theme> = {
  "&:hover, &:focus": { transform: "scale(1.05)" },
  "&:active": { transform: "scale(0.95)" },
};

export interface CommentPopoverButtonProps extends Omit<PropsAs<typeof Button>, "children"> {
  comment?: Comment;
  isVisible?: boolean;
}
export const CommentPopoverButton = React.forwardRef<HTMLButtonElement, CommentPopoverButtonProps>(
  ({ comment, isVisible, disabled, sx }, ref) => {
    return (
      <Button
        ref={ref}
        size="small"
        color={comment?.actionItem ? "error" : "primary"}
        sx={mergeSx(
          TRIGGER_SX,
          comment ? BADGE_TRIGGER_SX : undefined,
          isVisible ? undefined : HIDDEN_TRIGGER_SX,
          disabled ? INERT_TRIGGER_SX : INTERACTIVE_TRIGGER_SX,
          sx,
        )}>
        {!comment && (
          <>
            <Icons.AddComment /> <span className="sr-only">Add comment</span>
          </>
        )}

        {comment && (
          <>
            {comment?.actionItem ? <Icons.Flag /> : <Icons.Comment className="!size-3" />}#
            {comment?.badgeNumber}
          </>
        )}
      </Button>
    );
  },
);
